import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import {
  SEARCH_ALERTS_MODAL_PARAM,
  SEARCH_ALERTS_MODAL_TYPES,
} from '@xing-com/crate-jobs-constants';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';
import type { ExtractedFiltersEnabledFragment } from '@xing-com/crate-jobs-graphql';
import { useToggleSearchAlert } from '@xing-com/crate-jobs-hooks';
import {
  SearchAlertsErrorBanner,
  SearchAlertsSearchModal,
} from '@xing-com/crate-jobs-xinglets';
import {
  IconNotificationCenter,
  IconNotificationFilled,
} from '@xing-com/icons';
import { mediaTopBarVisible } from '@xing-com/layout-tokens';
import { spaceM, spaceS, spaceXL } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { AppliedFilters } from '../../shared';
import { MoreResults } from '../more-results';
import { NewJobsSeenBanner } from '../new-jobs-seen-banner';
import { ResultsHeader } from '../results-header';

type Props = {
  serps: SerpsRouteParams | null;
  hasSinceFilter?: boolean;
  extractedFilters: (ExtractedFiltersEnabledFragment | null)[];
};

export const EmptyState: React.FC<Props> = ({
  serps,
  hasSinceFilter,
  extractedFilters,
}) => {
  const { isAlertActive, isLoading, onToggleAlert } = useToggleSearchAlert();
  const { isLoggedIn } = useLoginState();
  const { setSearchParameter } = useSearchParameter();

  const handleOnToggle = (): void => {
    if (!isLoggedIn) {
      setSearchParameter(
        SEARCH_ALERTS_MODAL_PARAM,
        SEARCH_ALERTS_MODAL_TYPES.EMPTY_STATE
      );
      return;
    }

    onToggleAlert({
      nwtTrackingOptions: {
        elementDetail: 'empty_search',
      },
    });
  };

  const bodyKey = 'JOBS_SEARCH_AI_EMPTY_STATE_BANNER_SUBTITLE';

  const textButtonKey = isAlertActive
    ? 'JOBS_SEARCH_ALERT_CREATED'
    : 'JOBS_CREATE_SEARCH_ALERT';

  return hasSinceFilter ? (
    <>
      <ResultsHeader jobCount={-1} serps={serps} />
      <MediaConfined>
        <AppliedFilters />
      </MediaConfined>
      <NewJobsSeenBanner />
    </>
  ) : (
    <>
      <ResultsHeader jobCount={0} serps={serps} />
      <Body>
        <FormattedMessage id={bodyKey} />
        <ButtonSA
          id="search-alert-empty-results-button"
          variant="secondary"
          icon={isAlertActive ? IconNotificationFilled : IconNotificationCenter}
          loading={isLoading}
          onClick={handleOnToggle}
        >
          <FormattedMessage id={textButtonKey} />
        </ButtonSA>
      </Body>
      <MediaConfined>
        <AppliedFilters />
      </MediaConfined>
      <MoreResults extractedFilters={extractedFilters} />
      <SearchAlertsSearchModal />
      <SearchAlertsErrorBanner />
    </>
  );
};

const MediaConfined = styled.div`
  @media ${mediaTopBarVisible} {
    display: none;
  }
`;

const Body = styled(BodyCopy)`
  margin-top: -${spaceS};
  margin-bottom: ${spaceM};
  display: flex;
  gap: ${spaceXL};
  align-items: center;

  @media ${mediaTopBarVisible} {
    flex-direction: column;
    align-items: normal;
  }
`;

const ButtonSA = styled(Button)`
  flex-shrink: 0;

  @media ${mediaTopBarVisible} {
    width: fit-content;
  }
`;
